/**
 * PostgreSQL SQL dialect + compiler.
 *
 * PostgreSQL specifics:
 *   - identifiers quoted with double quotes: "col"
 *   - positional parameters `$1, $2, …` (pushed to `ctx.values`).
 *   - date grouping via `date_trunc('<granularity>', field)`.
 *   - upserts via `INSERT … ON CONFLICT (keys) DO UPDATE/NOTHING`.
 */
import { SqlCompiler, SqlParamContext, type SqlDialect } from './sql-compiler';
import type { FieldRefNode, GroupFieldNode } from '../../plan/query-plan';
import type { AssignmentOp } from '../../plan/mutation-plan';
import type { FieldType } from '../../input/input-types';

/** Cast applied to a text value extracted from JSON (`->>`), per field type. */
const POSTGRES_JSON_CAST: Partial<Record<FieldType, string>> = {
    number: 'numeric',
    boolean: 'boolean',
    date: 'timestamptz',
    // string: no cast — `->>` already returns text.
};

export class PostgresDialect implements SqlDialect {
    readonly engine = 'postgresql' as const;

    escapeIdentifier(identifier: string): string {
        // Quote the whole identifier with double quotes, escaping embedded ones.
        return `"${identifier.split('"').join('""')}"`;
    }

    renderFieldPath(field: FieldRefNode): string {
        // Chain `->` for intermediate keys and `->>` (text) for the last one,
        // then cast to the field's type. Keys are single-quoted string literals.
        const segments = field.nestedPath ?? [];
        const key = (k: string): string => `'${k.split("'").join("''")}'`;
        let expr = this.escapeIdentifier(field.field);
        segments.forEach((segment, i) => {
            expr += `${i === segments.length - 1 ? ' ->> ' : ' -> '}${key(
                segment,
            )}`;
        });
        expr = `(${expr})`;
        const cast = POSTGRES_JSON_CAST[field.type];
        return cast ? `${expr}::${cast}` : expr;
    }

    bindValue(value: unknown, ctx: SqlParamContext): string {
        ctx.values.push(value);
        return `$${ctx.values.length}`;
    }

    renderLiteral(value: unknown): string {
        if (value === null || value === undefined) {
            return 'NULL';
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        return `'${String(value).split("'").join("''")}'`;
    }

    renderDateGroup(field: FieldRefNode, group: GroupFieldNode): string {
        // TODO: apply the group's timezone `offset` when set.
        return `date_trunc('${
            group.granularity ?? 'day'
        }', ${this.escapeIdentifier(field.field)})`;
    }

    renderInsert(
        table: string,
        columns: string[],
        valuesSql: string,
        conflict: { keyColumns: string[] } | null,
    ): string {
        const cols = columns.map((c) => this.escapeIdentifier(c)).join(', ');
        let sql = `INSERT INTO ${table} (${cols}) VALUES ${valuesSql}`;
        if (conflict) {
            const keys = conflict.keyColumns
                .map((c) => this.escapeIdentifier(c))
                .join(', ');
            const updates = columns
                .filter((c) => !conflict.keyColumns.includes(c))
                .map(
                    (c) =>
                        `${this.escapeIdentifier(
                            c,
                        )} = EXCLUDED.${this.escapeIdentifier(c)}`,
                )
                .join(', ');
            sql += updates
                ? ` ON CONFLICT (${keys}) DO UPDATE SET ${updates}`
                : ` ON CONFLICT (${keys}) DO NOTHING`;
        }
        return sql;
    }

    renderDelete(table: string, where: string | null): string {
        return `DELETE FROM ${table}${where ? ` WHERE ${where}` : ''}`;
    }

    renderAssignment(
        column: string,
        op: AssignmentOp,
        value: string | null,
    ): string {
        switch (op) {
            case 'increment':
                return `${column} = ${column} + ${value}`;
            case 'append':
                return `${column} = array_append(${column}, ${value})`;
            case 'removeField':
                return `${column} = NULL`;
            case 'set':
            default:
                return `${column} = ${value}`;
        }
    }

    renderUpdate(
        table: string,
        assignments: string,
        where: string | null,
    ): string {
        return `UPDATE ${table} SET ${assignments}${
            where ? ` WHERE ${where}` : ''
        }`;
    }
}

export class PostgresCompiler extends SqlCompiler {
    constructor() {
        super(new PostgresDialect());
    }
}
